import type { BriefGateway, SubmissionReceipt } from '@/application/brief/BriefGateway';
import type { BriefDraft, BriefErrors } from '@/domain/brief/BriefDraft';
import { fail, type Result, succeed } from '@/domain/shared/Result';

export type SubmitBriefError =
  { readonly kind: 'invalid'; readonly errors: BriefErrors } | { readonly kind: 'unavailable' };

export interface SubmitBriefCommand {
  readonly draft: BriefDraft;
  /** Champ piège invisible : rempli uniquement par les robots. */
  readonly honeypot: string;
  readonly signal?: AbortSignal;
}

/** Cas d'usage : valider puis transmettre un brief. */
export class SubmitBrief {
  private readonly gateway: BriefGateway;

  constructor(gateway: BriefGateway) {
    this.gateway = gateway;
  }

  async execute(command: SubmitBriefCommand): Promise<Result<SubmissionReceipt, SubmitBriefError>> {
    const brief = command.draft.toBrief();
    if (!brief.ok) return fail({ kind: 'invalid', errors: brief.error });

    if (command.honeypot.trim() !== '') {
      // On répond comme si tout allait bien pour ne rien apprendre au robot.
      return succeed({ reference: 'ignored', receivedAt: new Date() });
    }

    try {
      return succeed(await this.gateway.submit(brief.value, command.signal));
    } catch (error: unknown) {
      if (command.signal?.aborted) throw error;
      return fail({ kind: 'unavailable' });
    }
  }
}
